// The planets as points of light. At true scale a planet a few thousand
// radii away covers less than a pixel and the rasteriser draws nothing,
// where the real sky shows Venus and Jupiter as its brightest "stars" and
// Saturn, Mars and Mercury among the first magnitude. Each planet is drawn
// with the star layers' point-spread function and exposure (src/stars.js) at
// its apparent magnitude from wherever the camera is
// (src/sky/planetMagnitude.js, phase and distance included), and hands over
// to its mesh as the disc grows from POINT_PX to FULL_PX across. At artistic
// scale the planets are drawn tens of times their true angular size, so a
// planet in another part of the sky was a dim, soft disc a few pixels wide
// where it should read as a bright star: there the point takes over from
// ARTISTIC_POINT_PX to ARTISTIC_FULL_PX, at its magnitude in the artistic
// layout (the caller's unitsPerAu).
import * as THREE from 'three';
import { createStarMaterial, STAR_REFERENCE_MAG } from '../stars.js';
import { planetMagnitude } from './planetMagnitude.js';
import { raDecToScene } from './frame.js';

const BODIES = ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune'];
// Unit-luminance colours: each body's own texture (public/textures/<name>.jpg)
// averaged in linear light over its area, so the dot is the colour of the
// disc it becomes; the Earth's with its cloud map laid over at its own cover
// (21%). Venus takes the cream its visible-light shader draws (its texture
// is coloured for other light).
export const PLANET_POINT_COLOURS = {
  mercury: [1.01, 0.997, 1.0],
  venus: [1.12, 1.0, 0.78],
  earth: [0.95, 0.993, 1.213],
  mars: [1.44, 0.913, 0.567],
  jupiter: [1.099, 0.985, 0.862],
  saturn: [1.203, 0.976, 0.637],
  uranus: [0.608, 1.098, 1.183],
  neptune: [0.671, 1.076, 1.218],
};
// Saturn's north pole (the IAU's, J2000, as scripts/site/sky-event-facts.js
// and src/photos/geometry.js use): the rings' opening to the camera. The
// viewer's own Saturn tilts about world Z, which is not where its pole is.
const SATURN_POLE = raDecToScene(40.589 / 15, 83.537);
const POINT_PX = 1.5;
const FULL_PX = 4;
const ARTISTIC_POINT_PX = 6;
const ARTISTIC_FULL_PX = 16;
// At artistic scale the light sits this share of the way to the camera:
// there the disc can cover most of the core, and 2% of a radius in front of
// it was finer than the depth buffer resolves at a few hundred units (near
// plane 0.3), so the night side won pixels of the core.
const ARTISTIC_LIFT = 0.05;
// A planet seen from nearby is far brighter than any star (the Earth from
// 0.3 AU is about magnitude -8), and through the stars' PSF its Moffat wing
// filled the whole 80 px sprite: a soft ball, where a bright planet looks
// like a very bright star. Past KNEE_MAG the drawn brightness rises at
// KNEE_SLOPE of the true one, so every planet keeps its order and reads as
// the sky's brightest star, a hard core with a short glow. magnitudes[]
// keeps the true values.
const KNEE_MAG = -2;
const KNEE_SLOPE = 0.2;
const drawnMag = (mag) => (mag < KNEE_MAG ? KNEE_MAG + (mag - KNEE_MAG) * KNEE_SLOPE : mag);

const _c = new THREE.Vector3(), _p = new THREE.Vector3(), _toCam = new THREE.Vector3();

export function createPlanetPoints(renderer) {
  const n = BODIES.length;
  const geometry = new THREE.BufferGeometry();
  const position = new THREE.BufferAttribute(new Float32Array(n * 3), 3);
  const color = new THREE.BufferAttribute(new Float32Array(n * 3), 3);
  const flux = new THREE.BufferAttribute(new Float32Array(n), 1);
  position.setUsage(THREE.DynamicDrawUsage);
  flux.setUsage(THREE.DynamicDrawUsage);
  BODIES.forEach((name, i) => color.setXYZ(i, ...PLANET_POINT_COLOURS[name]));
  geometry.setAttribute('position', position);
  geometry.setAttribute('color', color);
  geometry.setAttribute('flux', flux);
  const material = createStarMaterial(renderer);
  const points = new THREE.Points(geometry, material);
  points.name = 'planet-points';
  points.renderOrder = -998;
  points.frustumCulled = false;
  // Off by draw range, so the layer's visible flag stays with whoever hides
  // the sky (AR passthrough, a held famous photo).
  geometry.setDrawRange(0, 0);
  const magnitudes = {};

  // bodies: name -> Object3D (the planet's mesh, spinning about its pole);
  // unitsPerAu: the scene's scale; radiusOf(body): its drawn radius;
  // cssHeight: the canvas height in CSS pixels; the Sun is at the origin.
  // drawnShift(body), if given: how far above (+) or below where it is the
  // body was drawn last frame (spacetime mode lowers the planets onto its
  // sheet); the light goes there and the brightness stays the body's own.
  // artistic: the hand-over for artistic scale's enlarged discs.
  // reachOf(name), if given: how far the body's moons reach from it as drawn.
  function update({ active, camera, bodies, unitsPerAu, radiusOf, cssHeight, exposure, drawnShift, artistic = false, reachOf }) {
    geometry.setDrawRange(0, active ? n : 0);
    if (!active) return;
    material.uniforms.exposure.value = exposure;
    camera.getWorldPosition(_c);
    const fov = THREE.MathUtils.degToRad(camera.fov);
    BODIES.forEach((name, i) => {
      const body = bodies[name];
      if (!body) { flux.setX(i, 0); return; }
      body.getWorldPosition(_p);
      _toCam.subVectors(_c, _p);
      const dist = _toCam.length();
      const radius = radiusOf(body);
      const r = _p.length() / unitsPerAu, d = dist / unitsPerAu;
      const phase = r > 0 && dist > 0 ? THREE.MathUtils.radToDeg(Math.acos(THREE.MathUtils.clamp(-_p.dot(_toCam) / (r * unitsPerAu * dist), -1, 1))) : 0;
      let tilt = 0;
      if (name === 'saturn') {
        tilt = THREE.MathUtils.radToDeg(Math.asin(Math.min(1, Math.abs(SATURN_POLE.dot(_toCam) / dist))));
      }
      const mag = planetMagnitude(name, r, d, phase, tilt);
      magnitudes[name] = mag;
      if (drawnShift) {
        _p.y += drawnShift(body);
        _toCam.subVectors(_c, _p);
      }
      // The hand-over to the mesh goes by the disc as drawn.
      const drawnDist = _toCam.length();
      const discPx = (2 * Math.atan(radius / Math.max(drawnDist, radius)) / fov) * cssHeight;
      const show = artistic
        ? 1 - THREE.MathUtils.smoothstep(discPx, ARTISTIC_POINT_PX, ARTISTIC_FULL_PX)
        : 1 - THREE.MathUtils.smoothstep(discPx, POINT_PX, FULL_PX);
      flux.setX(i, Number.isFinite(mag) ? Math.pow(10, -0.4 * (drawnMag(mag) - STAR_REFERENCE_MAG)) * show : 0);
      // Just in front of the near surface, so the body's own pixel or two
      // never hides its light, while anything nearer still does. At artistic
      // scale ARTISTIC_LIFT of the way to the camera, and in front of its
      // moons (reachOf), whose enlarged discs, crossing the planet's few
      // pixels, put a coloured hole in the core of its light; at most
      // halfway.
      const forward = artistic ? Math.max(ARTISTIC_LIFT * drawnDist, reachOf ? reachOf(name) : 0) : 0;
      const lift = Math.max(radius * 1.02, Math.min(forward, 0.5 * drawnDist));
      _toCam.multiplyScalar(Math.min(1, lift / Math.max(drawnDist, 1e-9)));
      position.setXYZ(i, _p.x + _toCam.x, _p.y + _toCam.y, _p.z + _toCam.z);
    });
    position.needsUpdate = true;
    flux.needsUpdate = true;
  }

  return {
    mesh: points,
    update,
    magnitudes,
    setPixelRatio: (v) => { material.uniforms.pixelRatio.value = v; },
    dispose: () => { geometry.dispose(); material.dispose(); },
  };
}
